import produce from 'immer';
import { AnyAction } from 'redux';
import { ACTIONS, CLIENTS_ACTIONS } from '../actions/actions';
import {
  Client,
  ClientGroupList,
  ClientGroupOptions,
  SystemSetting,
  Property,
} from '../../types';

const initialState: {
  clients: { [id: string]: Client };
  clientUsers: { [id: string]: Client[] };
  clientCredentials: { [id: string]: Client };
  selectedClient?: string;
  clientCount: number;
  modules?: [];
  systemSettings: { [clientId: string]: SystemSetting[] };
  propertySettings: {
    [clientId: string]: { [propertyId: string]: SystemSetting[] };
  };
  editClient?: Client;
  editUser?: Client;
  editSetting?: {
    setting: SystemSetting;
    clientId: string;
  };
  editPropertySetting?: {
    setting: SystemSetting;
    clientId: string;
    propertyId: string;
  };
  clientProperties: { [clientId: string]: Property[] };
  clientGroupOptions?: ClientGroupOptions[];
  clientGroupList: ClientGroupList[];
  clientUserList: any[];
  groupUsers: { [id: string]: Client[] };
  isLoading: boolean;
} = {
  clients: {},
  clientCount: -1,
  selectedClient: undefined,
  modules: undefined,
  clientUsers: {},
  clientCredentials: {},
  editClient: undefined,
  editUser: undefined,
  editSetting: undefined,
  editPropertySetting: undefined,
  systemSettings: {},
  propertySettings: {},
  clientProperties: {},
  clientGroupOptions: undefined,
  clientGroupList: undefined,
  clientUserList: undefined,
  groupUsers: {},
  isLoading: false,
};
function clientReducer(state = initialState, action: AnyAction) {
  switch (action.type) {
    case ACTIONS.IS_LOADING:
      return { ...state, isLoading: action.payload };
    case CLIENTS_ACTIONS.ALL_CLIENTS: {
      return produce(state, (draftState) => {
        const clients = action.payload;

        draftState.clientCount = clients.length;
        clients.forEach((client: Client) => {
          draftState.clients[client.id] = client;
        });
      });
    }
    case CLIENTS_ACTIONS.UPDATE_CLIENT:
    case CLIENTS_ACTIONS.ADD_CLIENT: {
      return produce(state, (draftState) => {
        draftState.clients[action.payload.id] = action.payload;
      });
    }
    case CLIENTS_ACTIONS.UI_MODULES: {
      return produce(state, (draftState) => {
        draftState.modules = action.payload;
      });
    }
    case CLIENTS_ACTIONS.USERS_BY_CLIENT: {
      return produce(state, (draftState) => {
        const { clientId, users } = action.payload;
        draftState.clientUsers[clientId] = users;
      });
    }
    case CLIENTS_ACTIONS.UPDATE_USER:
    case CLIENTS_ACTIONS.ADD_USER: {
      return produce(state, (draftState) => {
        const user = action.payload;
        const { Salt, Hash, ...restUser } = user;
        if (draftState.clientUsers[user.ClientId]) {
          const index = draftState.clientUsers[user.ClientId].findIndex(
            (elem) => elem.id === user.id
          );
          if (index === -1) {
            draftState.clientUsers[user.ClientId].push(restUser);
          } else draftState.clientUsers[user.ClientId][index] = restUser;
        } else {
          draftState.clientUsers[user.ClientId] = [restUser];
        }
      });
    }
    case CLIENTS_ACTIONS.CREATE_YARDI_SIPP_CREDENTIALS:
    case CLIENTS_ACTIONS.UPDATE_YARDI_SIPP_CREDENTIALS: {
      return produce(state, (draftState) => {
        const { clientId, credentials } = action.payload;
        draftState.clientCredentials[clientId].sipp = credentials;
      });
    }

    case CLIENTS_ACTIONS.CREATE_ENTRATA_CREDENTIALS:
    case CLIENTS_ACTIONS.UPDATE_ENTRATA_CREDENTIALS: {
      return produce(state, (draftState) => {
        const { clientId, credentials } = action.payload;
        draftState.clientCredentials[clientId].entrataApi = credentials;
      });
    }
    case CLIENTS_ACTIONS.CREATE_SFTP_CREDENTIALS:
    case CLIENTS_ACTIONS.UPDATE_SFTP_CREDENTIALS: {
      return produce(state, (draftState) => {
        const { clientId, credentials } = action.payload;
        draftState.clientCredentials[clientId].sftp = credentials;
      });
    }
    case CLIENTS_ACTIONS.CREDENTIALS_BY_CLIENT: {
      return produce(state, (draftState) => {
        const { clientId, credentials } = action.payload;
        draftState.clientCredentials[clientId] = credentials;
      });
    }
    case CLIENTS_ACTIONS.SET_EDIT_CLIENT: {
      return produce(state, (draftState) => {
        draftState.editClient = action.payload;
      });
    }
    case CLIENTS_ACTIONS.SET_EDIT_USER: {
      return produce(state, (draftState) => {
        draftState.editUser = action.payload;
      });
    }
    case CLIENTS_ACTIONS.SYSTEM_SETTINGS: {
      return produce(state, (draft) => {
        draft.systemSettings[action.payload.clientId] = action.payload.settings;
      });
    }
    case CLIENTS_ACTIONS.PROPERTY_SETTINGS: {
      return produce(state, (draft) => {
        const { clientId, propertyId, settings } = action.payload;
        if (!draft.propertySettings[clientId])
          draft.propertySettings[clientId] = {};
        draft.propertySettings[clientId][propertyId] = settings;
      });
    }
    case CLIENTS_ACTIONS.SET_EDIT_SETTING: {
      return produce(state, (draft) => {
        draft.editSetting = action.payload;
      });
    }
    case CLIENTS_ACTIONS.SET_EDIT_PROPERTY_SETTING: {
      return produce(state, (draft) => {
        draft.editPropertySetting = action.payload;
      });
    }
    case CLIENTS_ACTIONS.UPDATE_SYSTEM_SETTINGS: {
      return produce(state, (draft) => {
        const { clientId, setting } = action.payload;

        draft.systemSettings[clientId] = draft.systemSettings[clientId].map(
          (prev) => {
            if (prev.id !== setting.id) return prev;
            else return { ...setting, spec: prev.spec };
          }
        );
      });
    }
    case CLIENTS_ACTIONS.UPDATE_PROPERTY_SETTINGS: {
      return produce(state, (draft) => {
        const { clientId, setting, propertyId } = action.payload;

        draft.propertySettings[clientId][propertyId] = draft.propertySettings[
          clientId
        ][propertyId].map((prev) => {
          if (prev.Key !== setting.Key) return prev;
          else return { ...prev, ...setting, spec: prev.spec };
        });
      });
    }
    case CLIENTS_ACTIONS.RESET_PROPERTY_SETTINGS: {
      return produce(state, (draft) => {
        const { clientId, propertyId } = action.payload;

        delete draft.propertySettings[clientId][propertyId];
      });
    }
    case CLIENTS_ACTIONS.CLIENT_PROPERTIES: {
      return produce(state, (draft) => {
        const { clientId, properties } = action.payload;
        draft.clientProperties[clientId] = properties;
      });
    }
    case CLIENTS_ACTIONS.GET_CLIENT_GROUP_OPTIONS: {
      return produce(state, (draftState) => {
        draftState.clientGroupOptions = action.payload;
      });
    }
    case CLIENTS_ACTIONS.GET_CLIENT_GROUP_LIST: {
      return produce(state, (draftState) => {
        draftState.clientGroupList = action.payload;
      });
    }
    case CLIENTS_ACTIONS.GET_CLIENT_USER_LIST: {
      return produce(state, (draftState) => {
        draftState.clientUserList = action.payload;
      });
    }
    case CLIENTS_ACTIONS.GROUP_USERS_BY_CLIENT: {
      return produce(state, (draftState) => {
        const { clientId, groupUsers } = action.payload;
        draftState.groupUsers[clientId] = groupUsers;
      });
    }
    case CLIENTS_ACTIONS.ADD_USER_TO_GROUP: {
      return produce(state, (draftState) => {
        const { ClientId, groupUsers } = action.payload;
        draftState.clientUsers[ClientId] = groupUsers.filter(
          (user) => user.IsAdded
        );
        draftState.groupUsers[ClientId] = groupUsers;
      });
    }
    case CLIENTS_ACTIONS.ADD_CLIENT_GROUP: {
      return produce(state, (draftState) => {
        const { clientId, existingClientId, clientGroup, groupUsers } =
          action.payload;
        draftState.clients[clientId].GroupId = clientGroup.GroupId;
        draftState.clients[existingClientId].GroupId = clientGroup.GroupId;
        draftState.groupUsers[clientId] = groupUsers;
      });
    }
    case CLIENTS_ACTIONS.REMOVE_USER_FROM_GROUP: {
      return produce(state, (draftState) => {
        const { ClientId, groupUsers } = action.payload;
        draftState.clientUsers[ClientId] = groupUsers.filter(
          (user) => user.IsAdded
        );
        draftState.groupUsers[ClientId] = groupUsers;
      });
    }
    case CLIENTS_ACTIONS.EXISTING_CLIENT_GROUP:
      return produce(state, (draftState) => {
        const { clientId, GroupId, groupUsers } = action.payload;
        draftState.clients[clientId].GroupId = GroupId;
        draftState.groupUsers[clientId] = groupUsers;
      });
    case CLIENTS_ACTIONS.REMOVE_CLIENT_FROM_GROUP:
      return produce(state, (draftState) => {
        // const { updatedClient, existingGroupUsers, clientGroups, lastClient } =
        //   action.payload;
        // draftState.clients[updatedClient.id].GroupId = null;
        // draftState.clientUsers[updatedClient.id] = existingGroupUsers;
        // draftState.clientGroupOptions = clientGroups;
        // if (lastClient) draftState.clients[lastClient.id].GroupId = null;
        window.location.reload();
      });
    case CLIENTS_ACTIONS.EDIT_CLIENT_GROUP:
    default:
      return state;
  }
}

export default clientReducer;
